import useTranslator from "../../hooks/useTranslator";
import styles from "./ApplicationCreatedModal.module.css";
import sm from "./ApplicationPendingModal.module.css";
import ApplicationPeopleSummary, {
  ApplicationMember,
} from "./ApplicationPeopleSummary";

type Props = {
  title: string;
  peopleCount: number;
  members?: ApplicationMember[];
  pendingCount: number;
  onProceed: () => void;
  onLater: () => void;
};

const Plane = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="#162b26"
    strokeWidth="1.6"
    strokeLinejoin="round"
  >
    <path d="M21 3L3 11l7 3 3 7 8-18z" />
    <path d="M10 14l8-8" />
  </svg>
);
const Card = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="#5b6d66"
    strokeWidth="1.5"
  >
    <rect x="3" y="6" width="18" height="12" rx="1.5" />
    <path d="M3 9.5h18" />
    <circle cx="15.5" cy="13.5" r="1.2" />
  </svg>
);
const Bag = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="#5b6d66"
    strokeWidth="1.5"
  >
    <path d="M7 8V6a5 5 0 0 1 10 0v2" />
    <rect x="4" y="8" width="16" height="11" rx="1.5" />
    <path d="M4 11h16" />
  </svg>
);
const Info = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="#5b6d66"
    strokeWidth="1.6"
  >
    <circle cx="12" cy="12" r="8" />
    <path d="M12 11v5" />
    <circle cx="12" cy="8" r="0.7" fill="#5b6d66" stroke="none" />
  </svg>
);

export default function ApplicationPendingModal({
  title,
  peopleCount,
  members,
  pendingCount,
  onProceed,
  onLater,
}: Props) {
  const { _t } = useTranslator();
  return (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <div className={styles.card}>
        <div className={styles.check}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M6 12.5 10 16.5 18 7.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h2>
          {_t("onboarding_application_created_title", "Application created")}
        </h2>
        <p className={styles.appTitle}>{title}</p>
        <p
          className={styles.subtitle}
          style={{ fontWeight: 700, color: "#162b26" }}
        >
          {_t(
            "onboarding_application_created_for_people",
            "You're making the application for {{count}} people",
            "translation",
            { count: peopleCount }
          )}
        </p>
        <ApplicationPeopleSummary members={members} />
        <div className={sm.whatNext}>
          <strong>
            {_t("onboarding_what_next_title", "What happens next")}
          </strong>
          <div className={sm.row}>
            <span className={sm.ico}>
              <Plane />
            </span>
            <div>
              <b>
                {_t("onboarding_what_next_invited_title", "Invited profiles")}
              </b>
              <p>
                {_t(
                  "onboarding_what_next_invited_desc",
                  "We'll email them automatically. They complete their own application."
                )}
              </p>
            </div>
          </div>
          <div className={sm.row}>
            <span className={sm.ico}>
              <Card />
            </span>
            <div>
              <b>
                {_t(
                  "onboarding_what_next_self_managed_title",
                  "Profiles you'll fill in"
                )}
              </b>
              <p>
                {_t(
                  "onboarding_what_next_self_managed_need",
                  "You'll need, for each person:"
                )}
              </p>
              <div className={sm.need}>
                <span className={sm.smallIco}>
                  <Card />
                </span>
                <span>
                  {_t("onboarding_what_next_need_id", "Their ID or passport")}
                </span>
              </div>
              <div className={sm.need}>
                <span className={sm.smallIco}>
                  <Bag />
                </span>
                <span>
                  {_t(
                    "onboarding_what_next_need_income",
                    "Proof of work and income (payslips, contract or invoices)"
                  )}
                </span>
              </div>
              <div className={sm.info}>
                <span className={sm.smallIco}>
                  <Info />
                </span>
                <span>
                  {_t(
                    "onboarding_what_next_consent_info",
                    "When you complete a profile, we'll email that person to confirm you had their permission, review their details, and accept the terms before we process their information."
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
        <button className={sm.cta} onClick={onProceed}>
          {_t(
            "onboarding_application_created_complete_pending",
            "Complete pending profiles"
          )}{" "}
          <span className={sm.badge}>
            {_t(
              "onboarding_application_created_pending_badge",
              "{{count}} pending",
              "translation",
              { count: pendingCount }
            )}
          </span>
        </button>
        <div className={sm.later}>
          {_t(
            "onboarding_application_created_later_prefix",
            "Or do it later from your"
          )}{" "}
          <button onClick={onLater} className={sm.link}>
            {_t("onboarding_application_created_dashboard_link", "dashboard")}
          </button>
        </div>
      </div>
    </div>
  );
}
